/**
 * Shared client-URL normalization for the Skin from URL feature.
 *
 * Single source of truth for what the stage door, the wizard/modal, and tests
 * accept as a skinnable client URL. Server-side SSRF validation still runs on
 * every fetch — this is UX-level normalization only.
 */

/**
 * Normalize raw user input into a skinnable https URL.
 *
 * Rules: trim; prepend `https://` when no scheme; upgrade `http://` to
 * `https://`; reject non-http(s) schemes and hostnames without a dot.
 *
 * @param {string} input
 * @returns {{ ok: true, url: string } | { ok: false, message: string }}
 */
export function normalizeSkinUrl(input) {
  const raw = typeof input === 'string' ? input.trim() : '';
  if (!raw) {
    return { ok: false, message: "Enter your client's website address" };
  }

  let candidate = raw;
  const schemeMatch = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(candidate);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme === 'http') {
      candidate = `https://${candidate.slice(schemeMatch[0].length).replace(/^\/\//, '')}`;
    } else if (scheme !== 'https') {
      return { ok: false, message: 'Only web addresses (https://) are supported' };
    }
  } else {
    candidate = `https://${candidate}`;
  }

  let parsed;
  try {
    parsed = new URL(candidate);
  } catch {
    return { ok: false, message: 'That does not look like a valid web address' };
  }

  if (parsed.protocol !== 'https:') {
    return { ok: false, message: 'Only web addresses (https://) are supported' };
  }
  if (!parsed.hostname.includes('.')) {
    return { ok: false, message: 'Enter a full domain like client.com' };
  }

  return { ok: true, url: parsed.href };
}
